// ===========================================
// Outstanding Invoices Table Component
// ===========================================
// Table showing outstanding invoices with color-coded aging buckets

import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Typography,
  Box,
  Pagination,
  TableSortLabel,
} from '@mui/material';

import { OutstandingInvoice } from '../../api/reports.api';
import { formatCurrency, formatDate } from '../../hooks/useReports';

// ===========================================
// TYPES
// ===========================================

interface OutstandingInvoicesTableProps {
  invoices: OutstandingInvoice[];
  loading?: boolean;
}

type SortField = 'invoiceNumber' | 'familyName' | 'total' | 'amountDue' | 'dueDate' | 'daysOverdue';
type SortOrder = 'asc' | 'desc';

// ===========================================
// HELPERS
// ===========================================

/**
 * Get aging bucket from days overdue
 */
function getAgingBucket(daysOverdue: number): '0-30' | '31-60' | '61-90' | '90+' {
  if (daysOverdue <= 30) return '0-30';
  if (daysOverdue <= 60) return '31-60';
  if (daysOverdue <= 90) return '61-90';
  return '90+';
}

/**
 * Get color for aging bucket
 */
function getAgingColor(bucket: string): 'success' | 'warning' | 'error' {
  if (bucket === '0-30') return 'success';
  if (bucket === '31-60') return 'warning';
  return 'error';
}

// ===========================================
// COMPONENT
// ===========================================

export default function OutstandingInvoicesTable({
  invoices,
  loading = false,
}: OutstandingInvoicesTableProps) {
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<SortField>('daysOverdue');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const rowsPerPage = 25;

  // Sort invoices
  const sortedInvoices = [...invoices].sort((a, b) => {
    let aValue: any = a[sortField];
    let bValue: any = b[sortField];

    // Handle date sorting
    if (sortField === 'dueDate') {
      aValue = new Date(aValue).getTime();
      bValue = new Date(bValue).getTime();
    }

    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  // Paginate
  const paginatedInvoices = sortedInvoices.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );
  const pageCount = Math.ceil(invoices.length / rowsPerPage);

  // Calculate total outstanding
  const totalOutstanding = invoices.reduce((sum, inv) => sum + inv.amountDue, 0);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          Loading outstanding invoices...
        </Typography>
      </Box>
    );
  }

  if (invoices.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          No outstanding invoices
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'invoiceNumber'}
                  direction={sortField === 'invoiceNumber' ? sortOrder : 'asc'}
                  onClick={() => handleSort('invoiceNumber')}
                >
                  Invoice #
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'familyName'}
                  direction={sortField === 'familyName' ? sortOrder : 'asc'}
                  onClick={() => handleSort('familyName')}
                >
                  Family Name
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortField === 'total'}
                  direction={sortField === 'total' ? sortOrder : 'asc'}
                  onClick={() => handleSort('total')}
                >
                  Total
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Amount Paid</TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortField === 'amountDue'}
                  direction={sortField === 'amountDue' ? sortOrder : 'asc'}
                  onClick={() => handleSort('amountDue')}
                >
                  Outstanding
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'dueDate'}
                  direction={sortField === 'dueDate' ? sortOrder : 'asc'}
                  onClick={() => handleSort('dueDate')}
                >
                  Due Date
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={sortField === 'daysOverdue'}
                  direction={sortField === 'daysOverdue' ? sortOrder : 'asc'}
                  onClick={() => handleSort('daysOverdue')}
                >
                  Days Overdue
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">Aging</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedInvoices.map((invoice) => {
              const agingBucket = getAgingBucket(invoice.daysOverdue);
              const agingColor = getAgingColor(agingBucket);

              return (
                <TableRow key={invoice.invoiceId}>
                  <TableCell>{invoice.invoiceNumber}</TableCell>
                  <TableCell>{invoice.familyName}</TableCell>
                  <TableCell align="right">{formatCurrency(invoice.total)}</TableCell>
                  <TableCell align="right">{formatCurrency(invoice.amountPaid)}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600} color="error.main">
                      {formatCurrency(invoice.amountDue)}
                    </Typography>
                  </TableCell>
                  <TableCell>{formatDate(invoice.dueDate.toString())}</TableCell>
                  <TableCell align="center">{invoice.daysOverdue}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={agingBucket}
                      color={agingColor}
                      size="small"
                      sx={{ minWidth: 60 }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Footer with total */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
        <Typography variant="body2" fontWeight={600}>
          Total Outstanding: {formatCurrency(totalOutstanding)}
        </Typography>

        {/* Pagination */}
        {pageCount > 1 && (
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        )}
      </Box>
    </Box>
  );
}
